/**
 * Después de restaurar una empresa: quién NO volvió a tener acceso y por qué.
 *
 * Restaurar sólo reactiva a las personas que archivar apagó Y que nadie tocó mientras tanto
 * (un cambio de rol, una baja a propósito). Las demás quedan como están y la plataforma tiene
 * que enterarse: si no, parece que «se restauró todo» y alguien sigue sin poder entrar.
 */

export interface PersonaSinRestaurar {
  membresiaId: string;
  persona: string | null;
  correo: string | null;
  motivo: 'MODIFICADA' | 'YA_ENCENDIDA' | 'BORRADA' | 'CUENTA_ELIMINADA';
}

const MOTIVO: Record<PersonaSinRestaurar['motivo'], string> = {
  MODIFICADA:
    'alguien cambió su acceso mientras la empresa estaba archivada (por ejemplo, su rol), así que no se reactivó sola',
  YA_ENCENDIDA: 'ya tenía el acceso encendido',
  BORRADA: 'su acceso se eliminó mientras la empresa estaba archivada',
  CUENTA_ELIMINADA:
    'su cuenta está eliminada, así que no se reactivó. Si se restaura la persona, su acceso a esta empresa hay que devolvérselo aparte',
};

export function AvisoDeRestauracion({ personas }: { personas: PersonaSinRestaurar[] }) {
  if (personas.length === 0) return null;
  return (
    <div
      role="alert"
      className="max-w-2xl space-y-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm"
    >
      <p className="font-medium">
        {personas.length === 1
          ? '1 persona no volvió a tener acceso'
          : `${personas.length} personas no volvieron a tener acceso`}
      </p>
      <ul className="list-disc space-y-1 pl-5 text-xs">
        {personas.map((p) => (
          <li key={p.membresiaId}>
            <strong>{p.persona ?? 'Una persona que ya no está en la empresa'}</strong>
            {p.correo ? ` (${p.correo})` : ''}: {MOTIVO[p.motivo]}.
          </li>
        ))}
      </ul>
      {personas.some((p) => p.motivo !== 'CUENTA_ELIMINADA') && (
        <p className="text-xs text-muted-foreground">
          Si corresponde que entren, reactivá su acceso desde la lista de personas de la empresa.
        </p>
      )}
      {personas.some((p) => p.motivo === 'CUENTA_ELIMINADA') && (
        <p className="text-xs text-muted-foreground">
          Las cuentas eliminadas se restauran desde Personas › Personas eliminadas; después se les
          devuelve el acceso a esta empresa desde su ficha.
        </p>
      )}
    </div>
  );
}
